import {
  CompanySectionsContext,
  type CompanySectionsState,
} from "@/contexts/company-sections-context";
import { CompanyDTO } from "@shaarp/common";
import {
  createContext,
  lazy,
  memo,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const TAB_COMPONENTS = {
  insights: lazy(() => import("./insights")),
  employees: lazy(() => import("./employees")),
  news: lazy(() => import("./news")),
  financials: lazy(() => import("./financials")),
  administrative: lazy(() => import("./administrative")),
  similars: lazy(() => import("./similars")),
  others: lazy(() => import("./others")),
};

export type CompanyTabContextType = {
  company: CompanyDTO;
  /** Each section's status: a card shows on its own (`CompanySectionCard`). */
  sections: CompanySectionsState["sections"];
  /** The page's run is over: the cards that read the whole company may start. */
  done: boolean;
  onEditSources: () => void;
};

const CompanyTabContext = createContext<CompanyTabContextType | null>(null);

/** Hook used by tab pages to access company data. */
export function useCompanyTabContext(): CompanyTabContextType {
  const ctx = useContext(CompanyTabContext);
  if (!ctx) throw new Error("useCompanyTabContext must be used within CompanyTabContent");
  return ctx;
}

/**
 * Renders all visited tabs and hides inactive ones with CSS.
 * This keeps tab components mounted across tab switches so that
 * AI-generated content, search results, and streams are preserved.
 * Keyed by company.id so it resets on company change.
 *
 * Uses display:none (hidden) for inactive tabs to avoid phantom height.
 */
export const CompanyTabContent = memo(function CompanyTabContent({
  company,
  sections,
  activeTab,
  onEditSources,
}: {
  company: CompanyDTO;
  sections: CompanySectionsState;
  activeTab: string;
  onEditSources: () => void;
}) {
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(
    () => new Set([activeTab]),
  );

  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      return new Set(prev).add(activeTab);
    });
  }, [activeTab]);

  return (
    <>
      {Object.entries(TAB_COMPONENTS).map(([tabKey, TabComponent]) =>
        visitedTabs.has(tabKey) ? (
          <CompanyTabPanel
            key={tabKey}
            TabComponent={TabComponent}
            company={company}
            sections={sections}
            isActive={activeTab === tabKey}
            onEditSources={onEditSources}
          />
        ) : null,
      )}
    </>
  );
});

/** Individual tab panel — memoized to avoid re-rendering inactive tabs. */
const CompanyTabPanel = memo(function CompanyTabPanel({
  TabComponent,
  company,
  sections,
  isActive,
  onEditSources,
}: {
  TabComponent: React.LazyExoticComponent<React.ComponentType>;
  company: CompanyDTO;
  sections: CompanySectionsState;
  isActive: boolean;
  onEditSources: () => void;
}) {
  // Without the visibility: switching tabs would hand every visited panel a
  // new value, and re-render the charts and lists it holds.
  const ctxValue = useMemo(
    () => ({
      company,
      sections: sections.sections,
      done: sections.done,
      onEditSources,
    }),
    [company, sections, onEditSources],
  );

  return (
    <div
      className={isActive ? "" : "hidden"}
      aria-hidden={!isActive}
    >
      <CompanyTabContext.Provider value={ctxValue}>
        <CompanySectionsContext value={sections}>
          <Suspense fallback={null}>
            <TabComponent />
          </Suspense>
        </CompanySectionsContext>
      </CompanyTabContext.Provider>
    </div>
  );
});
